// The salon's look on the booking screens: colour theme on <html>, and the brand header (logo, name, tagline).
import { useEffect, useState } from 'react';
import { DEFAULT_THEME } from '@core/brand.ts';
import { trimLogo } from '../owner/trim.ts';
import { useShop } from './boot.tsx';

const isLatin = (s: string) => /^[\x20-\x7e]*$/.test(s);

/** Applies the shop's colour theme to the whole page (dialogs and toasts included). */
export function useShopTheme(): void {
  const { shop } = useShop();
  const theme = shop.brand?.theme ?? DEFAULT_THEME;
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    return () => { delete document.documentElement.dataset.theme; };
  }, [theme]);
}

/** A logo this many times wider than tall already carries the shop name: it is shown wide, instead of the name. */
const WIDE_LOGO = 2.2;

export function ShopHeader() {
  const { shop, api } = useShop();
  const tagline = shop.brand?.tagline ?? null;
  const logo = shop.brand?.logoVersion ? api.logoUrl(shop.brand.logoVersion) : null;
  const [shape, setShape] = useState<'square' | 'wide' | 'failed' | null>(null);
  // Logos saved with a big empty border are trimmed here too, so the mark is not shown tiny.
  const [trimmed, setTrimmed] = useState<string | null>(null);
  const wide = shape === 'wide';
  const showLogo = logo && shape !== 'failed';

  function loaded(img: HTMLImageElement) {
    if (!trimmed) {
      try {
        const out = trimLogo(img);
        if (out !== img) { setTrimmed((out as HTMLCanvasElement).toDataURL('image/png')); return; }
      } catch { /* the browser refused to read the picture: show it as it is */ }
    }
    setShape(img.naturalWidth / Math.max(1, img.naturalHeight) >= WIDE_LOGO ? 'wide' : 'square');
  }

  return (
    <header className={`brand${wide ? ' brand-wide' : ''}`}>
      {showLogo && (
        <img className={wide ? 'brand-logo-wide' : 'brand-logo'} src={trimmed ?? logo} alt={wide ? shop.name : ''}
          crossOrigin="anonymous" style={shape ? undefined : { visibility: 'hidden' }}
          onLoad={(e) => loaded(e.currentTarget)} onError={() => setShape('failed')} />
      )}
      <div className="brand-text">
        {!wide && <div className={`brand-name${isLatin(shop.name) ? ' brand-latin' : ''}`}>{shop.name}</div>}
        {tagline && <div className={`brand-sub${isLatin(tagline) ? ' brand-latin' : ''}`}>{tagline}</div>}
      </div>
    </header>
  );
}
